<script setup lang="ts">
import { z } from 'zod'
import { toTypedSchema } from '@vee-validate/zod'
import { useForm, useField } from 'vee-validate'
import { Button } from '~/components/ui/button'
import { Input } from '~/components/ui/input'
import { LucideLoaderCircle } from 'lucide-vue-next'
import { FormControl, FormItem, FormLabel } from '~/components/ui/form'
import { useToast } from '~/components/ui/toast'
import { useApiCall } from '~/composables/useApi'

const { t } = useI18n()

const emit = defineEmits<{
  (e: 'success'): void
}>()

const forgotPasswordSchema = z.object({
  email: z
    .string({ required_error: useValidationMessage('requiredFieldEmail') })
    .email(useValidationMessage('emailInvalid'))
    .min(1, useValidationMessage('requiredFieldEmail'))
})

const { handleSubmit } = useForm({
  validationSchema: toTypedSchema(forgotPasswordSchema),
  validateOnMount: false
})

// Use useField for more control
const { value: emailValue, errorMessage: emailError, meta: emailMeta } = useField<string>('email')

const isLoading = ref(false)
const { toast } = useToast()

const onSubmit = handleSubmit(async (values) => {
  try {
    isLoading.value = true

    await useApiCall('/forgot-password', {
      method: 'POST',
      body: { email: values.email }
    })

    emit('success')

    toast({
      title: t('feature.auth.forgot_password.instructions_sent_title'),
      description: t('feature.auth.forgot_password.instructions_sent_description'),
      variant: 'success'
    })
  } catch (err: unknown) {
    const error = err as Error & { status?: number; data?: { message?: string } }
    let errorMessage = t('feature.auth.forgot_password.error_submit_description')

    if (error.status === 404) {
      errorMessage = t('feature.auth.forgot_password.user_not_found')
    } else if (error.status === 422) {
      errorMessage = t('feature.auth.forgot_password.invalid_email')
    } else if (error.status === 429) {
      errorMessage = t('feature.auth.forgot_password.too_many_requests')
    } else if (error.data?.message) {
      errorMessage = error.data.message
    } else if (error.message && !error.message.includes('POST') && !error.message.includes('404')) {
      errorMessage = error.message
    }

    toast({
      title: t('feature.auth.forgot_password.error_submit_title'),
      description: errorMessage,
      variant: 'destructive'
    })
  } finally {
    isLoading.value = false
  }
})
</script>

<template>
  <form class="space-y-6" @submit="onSubmit">
    <FormItem>
      <FormLabel>{{ $t('common.email') }}*</FormLabel>
      <FormControl>
        <Input v-model="emailValue" :placeholder="t('form.register.email_placeholder')" />
      </FormControl>
      <p v-if="emailMeta.touched && emailError" class="text-sm font-medium text-red-500">
        {{ emailError }}
      </p>
    </FormItem>

    <Button type="submit" class="w-full" :disabled="isLoading">
      <LucideLoaderCircle v-if="isLoading" class="mr-2 h-5 w-5 animate-spin" />
      {{ isLoading ? $t('common.submitting') : $t('feature.auth.forgot_password.submit_button') }}
    </Button>
  </form>
</template>
